import { useState } from "react";
import profileIllustration from "@/assets/froppio-profile.svg";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { ProfileFields } from "@/features/profile/components/ProfileFields";
import { useCreateProfile, useUpdateProfile } from "@/features/profile/hooks/profileQueries";
import type { Profile } from "@/features/profile/types/profileTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormPage } from "@/shared/components/ui/FormPage";
import { SetupHeader } from "@/shared/components/ui/SetupHeader";

interface ProfileOnboardingPageProps {
  profile?: Profile;
  onSaved?: (profile: Profile) => void;
}

export function ProfileOnboardingPage({ profile, onSaved }: ProfileOnboardingPageProps) {
  const { user } = useAuth();
  const createProfile = useCreateProfile();
  const updateProfile = useUpdateProfile();
  const mutation = profile ? updateProfile : createProfile;
  const [formData, setFormData] = useState({
    firstName: profile?.firstName ?? "",
    lastName: profile?.lastName ?? "",
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (mutation.isPending || !user) return;
    mutation.mutate({
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
    }, { onSuccess: onSaved });
  };

  return (
    <div className="h-dvh">
      <FormPage
        header={<SetupHeader title="Let's start with you" illustration={profileIllustration} step={1} stage="Profile" />}
        onSubmit={handleSubmit}
        narrow
        actions={
          <Button type="submit" size="touch" disabled={mutation.isPending || !user}>
            {mutation.isPending ? "Saving..." : profile ? "Save and review" : "Continue"}
          </Button>
        }
      >
        <FieldGroup disabled={mutation.isPending}>
          <ProfileFields value={formData} onChange={setFormData} email={user?.email} />
        </FieldGroup>
        {mutation.error && <ErrorState message={mutation.error.message || "We couldn't save your profile. Please try again."} />}
      </FormPage>
    </div>
  );
}
